import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle, HourglassEmpty, Cancel, CloudUpload, Pets, Check,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { SkeletonCard } from '../components/Skeletons';
import { COBALT, INK, MUTED, FONT_DISPLAY } from '../theme/kiff';
import { saIdError, proofOfAddressDateError } from '../utils/saId';

/**
 * Handler onboarding on the handler site: submit an application with the full
 * FICA documentation pack, then track its status. Once an admin approves it
 * (in the admin portal), the user becomes a handler and is sent to the
 * handler dashboard.
 *
 * The form is presented as a staged checklist (the handoff's onboarding
 * pattern): each step's state is derived live from the form, so progress is
 * visible without any extra state to keep in sync.
 *
 * FICA: handlers receive payouts, so South African law needs identity
 * verification — SA ID number (validated with the ID checksum before it
 * leaves the browser), a certified ID copy, a proof of address issued within
 * the last 3 months, and (optionally, for now) a bank confirmation letter.
 */
const HandlerApply = () => {
  const navigate = useNavigate();
  const { user, verifyToken } = useAuth();

  const [status, setStatus] = useState(null); // application status payload
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    motivation: '',
    experience_description: '',
    references: '',
    sa_id_number: '',
    id_document: null,
    proof_of_address: null,
    proof_of_address_date: '',
    bank_confirmation: null,
    background_check: null,
  });

  const loadStatus = async () => {
    try {
      const s = await apiService.getHandlerApplicationStatus();
      setStatus(s);
    } catch (err) {
      // A brand-new user simply has no application yet.
      setStatus({ has_application: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));
  const setFile = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.files?.[0] || null }));

  // Inline FICA validation, shown once the person has typed something so the
  // pristine form doesn't open covered in red.
  const idError = form.sa_id_number ? saIdError(form.sa_id_number) : null;
  const dateError = form.proof_of_address_date
    ? proofOfAddressDateError(form.proof_of_address_date)
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.motivation || !form.experience_description) {
      setError('Please tell us about your motivation and experience first.');
      return;
    }
    const ficaProblem =
      saIdError(form.sa_id_number) ||
      (!form.id_document && 'Please attach a certified copy of your ID.') ||
      (!form.proof_of_address && 'Please attach a proof of address (issued within the last 3 months).') ||
      proofOfAddressDateError(form.proof_of_address_date);
    if (ficaProblem) {
      setError(ficaProblem);
      return;
    }
    setSubmitting(true);
    try {
      await apiService.submitHandlerApplication(form);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goToDashboard = async () => {
    // Pull fresh flags so the handler routes unlock.
    await verifyToken();
    navigate('/handler/dashboard');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <SkeletonCard sx={{ minHeight: 120, mb: 3 }} />
        <SkeletonCard sx={{ minHeight: 320 }} />
      </Container>
    );
  }

  // Already an approved handler.
  if (user?.is_handler) {
    return (
      <StatusView
        icon={<CheckCircle sx={{ fontSize: 56 }} color="success" />}
        title="You're all set"
        body="Your handler account is active."
        action={<Button variant="contained" onClick={goToDashboard}>Go to dashboard</Button>}
      />
    );
  }

  const appStatus = status?.status;

  if (status?.has_application && appStatus === 'pending') {
    return (
      <StatusView
        icon={<HourglassEmpty sx={{ fontSize: 56 }} color="warning" />}
        title="Application under review"
        body="Thanks for applying! Our team is reviewing your details and documents — usually within 2 business days. We'll email you as soon as a decision is made."
      />
    );
  }

  if (status?.has_application && appStatus === 'approved') {
    return (
      <StatusView
        icon={<CheckCircle sx={{ fontSize: 56 }} color="success" />}
        title="You're approved!"
        body="Welcome to the PetTrek handler community."
        action={<Button variant="contained" onClick={goToDashboard}>Go to dashboard</Button>}
      />
    );
  }

  if (status?.has_application && appStatus === 'rejected') {
    return (
      <StatusView
        icon={<Cancel sx={{ fontSize: 56 }} color="error" />}
        title="Application not approved"
        body={
          status?.admin_notes ||
          'Unfortunately your application was not approved at this time. Please contact support if you have questions.'
        }
      />
    );
  }

  // Step states derive straight from the form: done → current → locked.
  const aboutDone = Boolean(form.motivation && form.experience_description);
  const ficaDone = Boolean(
    form.sa_id_number &&
      !saIdError(form.sa_id_number) &&
      form.id_document &&
      form.proof_of_address &&
      form.proof_of_address_date &&
      !proofOfAddressDateError(form.proof_of_address_date)
  );
  const steps = [
    {
      label: 'Tell us about you',
      note: 'Your motivation, and hands-on experience with pets — this is what our reviewers read first',
      done: aboutDone,
    },
    {
      label: 'FICA documents',
      note: 'SA ID number, certified ID copy, and a proof of address issued within the last 3 months',
      done: ficaDone,
    },
    {
      label: 'We review and verify',
      note: 'Identity checks plus a decision, usually within 2 business days',
      done: false,
    },
  ];
  const currentIndex = steps.findIndex((s) => !s.done);

  // No application yet — show the form.
  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Pets sx={{ fontSize: 56, color: COBALT, mb: 1 }} />
        <Typography variant="h1" component="h1">
          Become a handler
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Three steps, about 10 minutes. Handlers get paid, so South African
          law (FICA) asks us to verify who you are — have your ID and a recent
          proof of address ready.
        </Typography>
      </Box>

      {/* The staged checklist. */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2.5 }}>
          <Stack spacing={2}>
            {steps.map((step, i) => {
              const state = step.done ? 'done' : i === currentIndex ? 'current' : 'locked';
              return (
                <Box
                  key={step.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.75,
                    opacity: state === 'locked' ? 0.55 : 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: FONT_DISPLAY,
                      fontWeight: 800,
                      fontSize: 13,
                      ...(state === 'done'
                        ? { backgroundColor: '#C9F0D8', border: '2px solid #0A5C33', color: '#0A5C33' }
                        : state === 'current'
                          ? { backgroundColor: COBALT, border: `2px solid ${INK}`, color: '#FFF' }
                          : { border: `2px solid ${MUTED}`, color: MUTED }),
                    }}
                  >
                    {state === 'done' ? <Check sx={{ fontSize: 16 }} /> : i + 1}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 800,
                        fontSize: 13,
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.note}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={submit}>
            <Stack spacing={2.5}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label="Why do you want to be a handler?"
                value={form.motivation}
                onChange={setField('motivation')}
                multiline
                minRows={3}
                required
                fullWidth
              />
              <TextField
                label="Describe your experience with pets"
                value={form.experience_description}
                onChange={setField('experience_description')}
                multiline
                minRows={3}
                required
                fullWidth
              />
              <TextField
                label="References (optional)"
                value={form.references}
                onChange={setField('references')}
                multiline
                minRows={2}
                fullWidth
              />

              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 800,
                  fontSize: 13,
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  pt: 1,
                }}
              >
                FICA documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
                A certified copy is one stamped by the police, a bank, or a
                commissioner of oaths. Proof of address can be a utility bill,
                bank statement or lease — issued within the last 3 months.
              </Typography>

              <TextField
                label="SA ID number"
                value={form.sa_id_number}
                onChange={setField('sa_id_number')}
                required
                fullWidth
                error={Boolean(idError)}
                helperText={idError || '13 digits, as printed on your ID'}
                inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              />

              <FileField
                label="Certified ID copy"
                required
                file={form.id_document}
                onChange={setFile('id_document')}
              />
              <FileField
                label="Proof of address"
                required
                file={form.proof_of_address}
                onChange={setFile('proof_of_address')}
              />
              <TextField
                label="Proof of address issue date"
                type="date"
                value={form.proof_of_address_date}
                onChange={setField('proof_of_address_date')}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={Boolean(dateError)}
                helperText={
                  dateError || 'The date printed on the document — must be within the last 3 months'
                }
              />
              <FileField
                label="Bank confirmation letter (optional)"
                file={form.bank_confirmation}
                onChange={setFile('bank_confirmation')}
              />
              <FileField
                label="Background check (optional)"
                file={form.background_check}
                onChange={setFile('background_check')}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Submit application'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

const FileField = ({ label, file, onChange, required }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
      {label}
      {required && ' *'}
    </Typography>
    <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
      {file ? 'Change file' : 'Upload'}
      <input
        hidden
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        aria-label={label}
        onChange={onChange}
      />
    </Button>
    {file && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
        {file.name}
      </Typography>
    )}
  </Box>
);

const StatusView = ({ icon, title, body, action }) => (
  <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
    <Card>
      <CardContent sx={{ textAlign: 'center', py: 6 }}>
        {icon}
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: action ? 3 : 0 }}>
          {body}
        </Typography>
        {action}
      </CardContent>
    </Card>
  </Container>
);

export default HandlerApply;
